import { Hidden } from '@mui/material'
import { BetaTag } from 'src/components/global/BetaTag/BetaTag'
import { EnhancedTag } from 'src/components/global/EnhancedTag/EnhancedTag'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
import { StartupTag } from 'src/components/global/StartupTag/StartupTag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { LinkWithOrigin } from 'src/components/LinkWithOrigin/LinkWithOrigin'
import {
  IAlspSolutionDataFragment,
  IConsultancySolutionDataFragment,
  IProductSolutionDataFragment,
} from 'src/graphql/generated/hooks'
import { isAlspSolution, isConsultancySolution, isProductSolution } from 'src/utils/SolutionDataFragment'
import { isConsolidationProductSolution } from 'src/utils/SolutionDataFragment'

interface SolutionTitleProps {
  solution: IAlspSolutionDataFragment | IConsultancySolutionDataFragment | IProductSolutionDataFragment
}

export const SolutionTitle: React.FC<SolutionTitleProps> = ({ solution }) => {
  const isPremium = !!solution.attributes?.isPremium
  const isEnhanced = isPremium || !!solution.attributes?.isEnhanced
  const isStartup = !!solution.attributes?.isStartup
  const isBeta = !!solution.attributes?.isBeta
  const name = isAlspSolution(solution)
    ? solution.attributes?.alspData?.name
    : isConsultancySolution(solution)
      ? solution.attributes?.consultantData?.name
      : isProductSolution(solution)
        ? solution.attributes?.productData?.name
        : null
  const company = solution.attributes?.company?.data || null
  const shouldShowCompany = company?.attributes?.slug && company?.attributes?.name
  const serviceName = isAlspSolution(solution)
    ? solution.attributes?.alspData?.serviceName
    : isConsultancySolution(solution)
      ? solution.attributes?.consultantData?.serviceName
      : isProductSolution(solution)
        ? solution.attributes?.productData?.tool
        : null
  const shouldShowName = serviceName && !shouldShowCompany && name !== serviceName

  const isConsolidation = isConsolidationProductSolution(solution)
  const originalTargetName = (
    <>
      {isConsolidation &&
        solution.attributes?.consolidationData?.buyer != null &&
        solution.attributes?.consolidationData?.originalTargetName != null && (
          <div className="text-[15px]">
            &nbsp;&nbsp;(Previous known as &nbsp;
            <span className="font-bold">{solution.attributes?.consolidationData?.originalTargetName})</span>
          </div>
        )}
    </>
  )

  return (
    <div className="flex flex-col items-center md:items-start">
      <h1 className="pb-[0.125rem] text-[1.5rem] font-bold font-poppins text-[#202A35]">
        {serviceName ? serviceName : name}
      </h1>
      <Hidden mdDown>
        <ThemeOnly theme="legaltech">
          <div className="inline-flex items-center space-x-[10px]">
            {isPremium && <PremiumTag variant="yellow" />}
            {!isPremium && isEnhanced && <EnhancedTag />}
            {isStartup && <StartupTag />}
            {isBeta && <BetaTag />}
          </div>
        </ThemeOnly>
        <ThemeOnly theme="ilta">
          <div className="inline-flex items-center space-x-[10px]">{(isPremium || isEnhanced) && <EnhancedTag />}</div>
        </ThemeOnly>
      </Hidden>
      {shouldShowName && (
        <h1 className="pt-[0.5rem] pb-[0.625rem] text-[19px] font-primary text-[#333333] flex items-center">
          by&nbsp;<span className="text-hero font-bold">{name}</span> {originalTargetName}
        </h1>
      )}
      {shouldShowCompany && (
        <div className="pt-[0.5rem] pb-[0.625rem] text-[16px] font-medium font-karla text-darkgray leading-[15px] flex">
          <div className="flex flex-row items-center">
            by&nbsp;
            <LinkWithOrigin toUrl={'/companies/' + company.attributes?.slug} solutionName={serviceName || name || ''}>
              <span className="cursor-pointer text-base font-bold">{company.attributes?.name}</span>
            </LinkWithOrigin>
          </div>
        </div>
      )}
    </div>
  )
}
